import { IconButton } from '@/components/ui/icon-button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/text'
import { DialogContainer } from '@components'
import { ArrowLeft, ArrowRight } from 'lucide-solid'
import {
  type Accessor,
  type JSX,
  type Setter,
  Show,
  createEffect,
  createSignal,
  on,
  onCleanup,
} from 'solid-js'
import { css } from 'styled-system/css'
import { Box } from 'styled-system/jsx'

export const ZoomableImage = (props: {
  src?: string
  title?: string
  children?: JSX.Element
  isOpen?: Accessor<boolean> | (() => boolean)
  setIsOpen?: Setter<boolean> | ((value: boolean) => void)
  next?: () => void
  prev?: () => void
}) => {
  const [localIsOpen, setLocalIsOpen] = createSignal(false)
  const [isLoaded, setIsLoaded] = createSignal(false)
  let imgRef: HTMLImageElement | undefined

  const isOpen = props.isOpen ? props.isOpen : localIsOpen
  const setIsOpen = props.setIsOpen ? props.setIsOpen : setLocalIsOpen

  // Reset loading state ONLY when the src changes later on (defer the first run)
  createEffect(
    on(
      () => props.src,
      () => setIsLoaded(false),
      { defer: true }
    )
  )

  // Safety net for cached images
  createEffect(() => {
    if (props.src && imgRef?.complete) {
      setIsLoaded(true)
    }
  })

  createEffect(() => {
    if (!isOpen() || (!props.prev && !props.next)) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && props.prev) {
        e.preventDefault()
        props.prev()
      } else if (e.key === 'ArrowRight' && props.next) {
        e.preventDefault()
        props.next()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    onCleanup(() => window.removeEventListener('keydown', handleKeyDown))
  })

  return (
    <>
      <DialogContainer
        padding="0"
        size={'cover'}
        isOpen={isOpen()}
        setIsOpen={setIsOpen}
      >
        <img
          alt={props.src}
          class={css({
            width: '100%',
            height: '100%',
            maxWidth: '100vw',
            maxHeight: '100vh',
            objectFit: 'contain',
            objectPosition: 'center',
          })}
          src={props.src}
        />
        <Show when={props?.title}>
          <Text
            position="absolute"
            zIndex={1}
            textStyle="xs"
            bottom={0}
            p={1}
            bg="bg.canvas"
            borderRadius="xs"
          >
            {props?.title}
          </Text>
        </Show>
        <Show when={props.prev && props.next}>
          <Box
            class={css({
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            })}
          >
            <IconButton
              onClick={props.prev}
              colorPalette="bg"
              variant="solid"
              size="xs"
              h="6"
              w="6"
            >
              <ArrowLeft />
            </IconButton>
            <IconButton
              onClick={props.next}
              colorPalette="bg"
              variant="solid"
              size="xs"
              h="6"
              w="6"
            >
              <ArrowRight />
            </IconButton>
          </Box>
        </Show>
      </DialogContainer>

      <Box
        class={css({
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'grid',
          justifyContent: 'stretch',
          alignItems: 'center',
          cursor: 'zoom-in',
        })}
        onClick={() => setIsOpen(true)}
      >
        <Show
          when={props.children}
          fallback={
            <>
              {/* Image is always rendered to trigger network requests & load events */}
              <img
                ref={imgRef}
                alt={props.src}
                class={css({
                  objectFit: 'cover',
                  height: '100%',
                  width: '100%',
                })}
                src={props.src}
                onload={() => setIsLoaded(true)}
                onerror={() => setIsLoaded(true)}
              />
              {/* Skeleton sits on top, and is violently unmounted when isLoaded flips to true */}
              <Show when={!isLoaded()}>
                <Skeleton
                  class={css({
                    position: 'absolute',
                    inset: 0,
                    height: '100%',
                    width: '100%',
                    pointerEvents: 'none', // ensures it doesn't block the click to zoom
                  })}
                />
              </Show>
            </>
          }
        >
          {props.children}
        </Show>

        <Show when={props?.title}>
          <Text
            position="absolute"
            zIndex={1}
            textStyle="xs"
            bottom={0}
            p={1}
            bg="bg.canvas"
            borderRadius="xs"
          >
            {props?.title}
          </Text>
        </Show>
      </Box>
    </>
  )
}
